import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { SectionHead } from "@/components/site/SectionHead";
import { QuoteButton } from "@/components/site/Cta";
import { Reveal } from "@/components/site/Reveal";
import { CITIES, TEXAS_PATH } from "@/content/site";

const slug = (n) => n.toLowerCase().replace(/\s+/g, "-");

const TexasSvg = ({ active, setActive }) => {
  const a = CITIES.find((c) => c.name === active);
  return (
    <svg viewBox="0 0 532 432" className="w-full" role="img" aria-label="Map of Texas with example service areas">
      <defs>
        <pattern id="tx-grid" width="12" height="12" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="0.8" fill="rgba(209,213,219,0.22)" /></pattern>
        <linearGradient id="tx-fill" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#1a1f28" /><stop offset="1" stopColor="#0c0e12" /></linearGradient>
        <filter id="tx-glow"><feGaussianBlur stdDeviation="6" /></filter>
      </defs>
      <path d={TEXAS_PATH} fill="rgba(245,166,35,0.25)" filter="url(#tx-glow)" />
      <path d={TEXAS_PATH} fill="url(#tx-fill)" />
      <path d={TEXAS_PATH} fill="url(#tx-grid)" />
      <motion.path d={TEXAS_PATH} fill="none" stroke="#f5a623" strokeWidth="1.4" strokeLinejoin="round"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 3, ease: "easeInOut" }} />
      {CITIES.map((c) => {
        const on = c.name === active;
        return (
          <g key={c.name} data-testid={`texas-map-pin-${slug(c.name)}`} onMouseEnter={() => setActive(c.name)} onClick={() => setActive(c.name)} className="cursor-pointer">
            <circle cx={c.x} cy={c.y} r="9" fill="transparent" />
            <circle cx={c.x} cy={c.y} r="3" fill="#f5a623" className="pin-pulse" opacity={on ? 1 : 0.5} />
            <circle cx={c.x} cy={c.y} r={on ? 4.2 : 2.6} fill={on ? "#ffd27a" : "#f5a623"} stroke="#08090a" strokeWidth="1" />
          </g>
        );
      })}
      {a && (
        <g pointerEvents="none">
          <line x1={a.x} y1={a.y} x2={a.x - 60} y2={a.y - 46} stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
          <rect x={a.x - 60 - 112} y={a.y - 46 - 15} width="112" height="26" rx="13" fill="rgba(8,9,10,0.92)" stroke="rgba(245,166,35,0.6)" />
          <text x={a.x - 60 - 56} y={a.y - 46 + 2} textAnchor="middle" fill="#fff" fontSize="11" fontFamily="JetBrains Mono, monospace">{a.name}</text>
        </g>
      )}
    </svg>
  );
};

export const TexasMap = () => {
  const [active, setActive] = useState("Austin");
  return (
    <section id="service-areas" data-testid="texas-map-section" className="relative overflow-hidden bg-[#0b0c0e] py-24 sm:py-32">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-center lg:px-12">
        <div className="lg:col-span-5">
          <SectionHead index="08" eyebrow="Service Area" lines={["Serving Texas", "from top", "to bottom"]}
            sub="Solar Energy Doc proudly serves homeowners and businesses throughout Texas." />
          <Reveal delay={0.1}>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400">Example service areas</p>
            <ul className="mt-4 flex flex-wrap gap-2" data-testid="texas-city-list">
              {CITIES.map((c) => (
                <li key={c.name}>
                  <button type="button" onMouseEnter={() => setActive(c.name)} onClick={() => setActive(c.name)} data-testid={`city-chip-${slug(c.name)}`}
                    className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-[background-color,border-color,color] duration-300 ${
                      c.name === active ? "border-amber-400 bg-amber-400 text-black" : "border-white/12 bg-white/[0.03] text-zinc-200 hover:border-amber-400/50"}`}>
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-zinc-300">
              <span className="font-semibold text-white">Don't see your city? Contact us—we may still be able to help.</span> Listed cities are examples; please contact us to confirm availability for your address.
            </p>
            <div className="mt-8"><QuoteButton testId="areas-cta">Check Service Availability</QuoteButton></div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7">
          <div className="relative rounded-[32px] border border-white/10 bg-[#08090a] p-5 sm:p-10">
            <div className="absolute left-5 top-5 sm:left-8 sm:top-8 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-amber-400" />
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-300" data-testid="map-active-city">{active}, TX</span>
            </div>
            <div className="pt-8"><TexasSvg active={active} setActive={setActive} /></div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
